import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Space, Modal, Form, Select, message, Tag, Typography, Switch, Image, Input, InputNumber, Popconfirm, Spin, AutoComplete } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProductSearchSelector from '../utils/ProductSearchSelector';

const { Option } = Select;
const { Text } = Typography;
const { Search } = Input;

// 获取API基础URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

// API调用
const specialPriceAPI = {
  getAll: (params) => axios.get(`${API_BASE_URL}/special-prices`, {
    params,
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }),
  getById: (id) => axios.get(`${API_BASE_URL}/special-prices/${id}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }),
  create: (data) => axios.post(`${API_BASE_URL}/special-prices`, data, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }),
  update: (id, data) => axios.put(`${API_BASE_URL}/special-prices/${id}`, data, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }),
  delete: (id) => axios.delete(`${API_BASE_URL}/special-prices/${id}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }),
  toggleEnable: (id) => axios.put(`${API_BASE_URL}/special-prices/${id}/toggle-enable`, {}, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  })
};

const usersAPI = {
  getAll: (params) => axios.get(`${API_BASE_URL}/users`, {
    params,
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  })
};

const SpecialPrice = () => {
  const navigate = useNavigate();
  const [specialPrices, setSpecialPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSpecialPrice, setCurrentSpecialPrice] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [variants, setVariants] = useState([]);
  const [form] = Form.useForm();
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userOptionMap, setUserOptionMap] = useState({});
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [productSelectorVisible, setProductSelectorVisible] = useState(false);

  const formatUserLabel = (user) => {
    if (!user) return '';
    const detail = user.name || user.email;
    return detail ? `${user.username} (${detail})` : user.username;
  };

  const userSelectOptions = useMemo(() => {
    const base = userOptions || [];
    const extended =
      selectedUser && !base.some(user => user?.id === selectedUser)
        ? [...base, userOptionMap[selectedUser]].filter(Boolean)
        : base;
    return extended
      .filter(Boolean)
      .map(user => ({
        value: String(user.id),
        label: formatUserLabel(user),
        user,
      }));
  }, [userOptions, userOptionMap, selectedUser]);

  // 分页相关状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
  });

  useEffect(() => {
    fetchSpecialPrices();
  }, []);


  const fetchSpecialPrices = async (page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      const response = await specialPriceAPI.getAll({ page, page_size: pageSize });
      
      setSpecialPrices(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        current: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      }));
    } catch (error) {
      message.error('获取特殊价格列表失败');
      
    } finally {
      setLoading(false);
    }
  };

  const handleUserSearch = async (value) => {
    const query = (value || '').trim();
    setUserSearchInput(value);
    if (!query) {
      setUserOptions([]);
      return;
    }

    try {
      setUserSearchLoading(true);
      const response = await usersAPI.getAll({
        page: 1,
        page_size: 20,
        role: 'dropshipper',
        search: query,
      });
      const userList = (response.data?.data || []).filter(user => user.role === 'dropshipper');
      setUserOptions(userList);
      setUserOptionMap(prev => {
        const next = { ...prev };
        userList.forEach(user => {
          next[user.id] = user;
        });
        return next;
      });
    } catch (error) {
      message.error('搜索用户失败');
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setCurrentSpecialPrice(null);
    setSelectedUser(null);
    setSelectedProduct(null);
    setProductDetails(null);
    setVariants([]);
    setUserSearchInput('');
    setUserOptions([]);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = async (record) => {
    try {
      setIsEditMode(true);
      setLoading(true);
      
      // 获取完整的特殊价格记录（包含产品原始variants）
      const response = await specialPriceAPI.getById(record.id);
      const fullRecord = response.data;
      
      setCurrentSpecialPrice(fullRecord);
      setSelectedUser(fullRecord.dropshipper_id);
      setSelectedProduct(fullRecord.product?.id);
      if (fullRecord.dropshipper) {
        setUserOptionMap(prev => ({
          ...prev,
          [fullRecord.dropshipper.id]: fullRecord.dropshipper,
        }));
        setUserOptions(prev => {
          if (prev.some(user => user.id === fullRecord.dropshipper.id)) {
            return prev;
          }
          return [...prev, fullRecord.dropshipper];
        });
        setUserSearchInput(formatUserLabel(fullRecord.dropshipper));
      }
      
      // 编辑模式：直接使用特殊价格记录中的产品信息和variants
      if (fullRecord.product) {
        setProductDetails(fullRecord.product);
        // 使用特殊价格中的variants（保持已设置的特殊价格）
        setVariants(fullRecord.variants || []);
      }
      
      form.setFieldsValue({
        dropshipper_id: fullRecord.dropshipper_id,
        product_id: fullRecord.product?.id,
        enable: fullRecord.enable
      });
      
      setModalVisible(true);
    } catch (error) {
      message.error('获取记录详情失败');
      
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await specialPriceAPI.delete(id);
      message.success('删除成功');
      fetchSpecialPrices();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleToggleEnable = async (id) => {
    try {
      const response = await specialPriceAPI.toggleEnable(id);
      message.success(response.data.message);
      fetchSpecialPrices();
    } catch (error) {
      message.error('状态切换失败');
    }
  };

  const handleUserSelect = (value, option) => {
    if (!value) {
      setSelectedUser(null);
      form.setFieldsValue({ dropshipper_id: undefined });
      setUserSearchInput('');
      return;
    }

    const userId = Number(value);
    if (!userId) {
      setSelectedUser(null);
      form.setFieldsValue({ dropshipper_id: undefined });
      setUserSearchInput('');
      return;
    }

    const matchedUser =
      option?.user ||
      userOptions.find(user => user.id === userId) ||
      userOptionMap[userId];

    setSelectedUser(userId);
    form.setFieldsValue({ dropshipper_id: userId });

    if (matchedUser) {
      setUserOptionMap(prev => ({
        ...prev,
        [userId]: matchedUser,
      }));
    }

    if (selectedProduct) {
      checkExistingRecord(userId, selectedProduct);
    }
  };

  const openProductSelector = () => {
    setProductSelectorVisible(true);
  };

  const closeProductSelector = () => {
    setProductSelectorVisible(false);
  };

  const handleProductSelected = async (productId, productData) => {
    closeProductSelector();
    
    setSelectedProduct(productId);
    
    // 清空之前的数据
    setProductDetails(null);
    setVariants([]);
    
    // 使用ProductSearchSelector传入的产品数据
    if (productData && productData.variants && productData.variants.length > 0) {
      setProductDetails(productData);
      setVariants(productData.variants.map(v => ({ ...v })));
      message.success(`已加载 ${productData.variants.length} 个SKU`);
      
      // 设置表单字段
      form.setFieldsValue({ product_id: productId });
    } else if (productData) {
      setProductDetails(productData);
      setVariants([]);
      message.warning('该商品暂无SKU信息');
      
      // 设置表单字段
      form.setFieldsValue({ product_id: productId });
    } else {
      message.error('未找到商品信息');
    }
    
    // 检查是否已选择用户，如果是，检查是否存在记录
    if (selectedUser) {
      checkExistingRecord(selectedUser, productId);
    }
  };

  const checkExistingRecord = (userId, productId) => {
    // 检查是否已存在相同用户和产品的记录
    const existing = specialPrices.find(
      sp => sp.dropshipper_id === userId && sp.product?.id === productId && 
            (!currentSpecialPrice || sp.id !== currentSpecialPrice.id)
    );
    
    if (existing) {
      message.warning('该用户的该商品已存在特殊价格记录');
    }
  };

  const handleVariantPriceChange = (index, newPrice) => {
    const newVariants = [...variants];
    newVariants[index] = {
      ...newVariants[index],
      price: newPrice
    };
    setVariants(newVariants);
  };

  // 批量价格调整
  const [bulkPricePercentage, setBulkPricePercentage] = useState(20);

  const handleBulkPriceAdjustment = (isIncrease) => {
    if (!bulkPricePercentage || bulkPricePercentage <= 0) {
      message.warning('请输入有效的百分比');
      return;
    }

    const newVariants = variants.map(variant => {
      const currentPrice = parseFloat(variant.price) || 0;
      const adjustment = currentPrice * (bulkPricePercentage / 100);
      const newPrice = isIncrease ? currentPrice + adjustment : currentPrice - adjustment;
      
      return {
        ...variant,
        price: Math.max(0, parseFloat(newPrice.toFixed(2))) // 确保价格不为负数
      };
    });

    setVariants(newVariants);
    message.success(`已${isIncrease ? '加价' : '减价'} ${bulkPricePercentage}%`);
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        dropshipper_id: values.dropshipper_id,
        product_id: values.product_id,
        variants: variants,
        enable: values.enable !== false
      };
      
      if (isEditMode) {
        await specialPriceAPI.update(currentSpecialPrice.id, {
          variants: variants,
          enable: values.enable !== false
        });
        message.success('更新成功');
      } else {
        await specialPriceAPI.create(data);
        message.success('创建成功');
      }
      
      setModalVisible(false);
      fetchSpecialPrices();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || '操作失败';
      message.error(errorMsg);
    }
  };

  // 处理分页变化
  const handleTableChange = (paginationConfig) => {
    const { current, pageSize } = paginationConfig;
    fetchSpecialPrices(current, pageSize);
  };

  // 计算规格数量
  const countVariants = (variantsData) => {
    if (!variantsData) return 0;
    if (Array.isArray(variantsData)) {
      return variantsData.length;
    }
    return 0;
  };

  const columns = [
    {
      title: '用户名',
      dataIndex: ['dropshipper', 'username'],
      key: 'username',
      render: (username) => username || '-',
    },
    {
      title: '封面图片',
      dataIndex: 'product',
      key: 'cover_picture',
      width: 80,
      render: (product) => {
        if (!product) return '-';
        
        const coverImage = product.images?.find(img => img.id === product.cover_picture);
        const firstImage = product.images?.[0];
        const imageToShow = coverImage || firstImage;
        
        if (imageToShow) {
          return (
            <Image
              src={`${API_BASE_URL}/products/${product.id}/images/${imageToShow.id}`}
              width={40}
              height={40}
              style={{ objectFit: 'cover', borderRadius: '4px' }}
              preview={false}
            />
          );
        }
        
        return (
          <div style={{ 
            width: '40px', 
            height: '40px', 
            backgroundColor: '#f0f0f0', 
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            color: '#999'
          }}>
            无图片
          </div>
        );
      },
    },
    {
      title: '商品名称',
      dataIndex: ['product', 'name'],
      key: 'product_name',
      ellipsis: true,
      render: (name, record) => {
        if (!name) return '-';
        return (
          <Button 
            type="link" 
            style={{ padding: 0, height: 'auto' }}
            onClick={() => window.open(`/product/${record.product?.id}`, '_blank')}
          >
            {name}
          </Button>
        );
      },
    },
    {
      title: '分类',
      dataIndex: ['product', 'category', 'name'],
      key: 'category',
      render: (categoryName) => categoryName || '未分类',
    },
    {
      title: '详细信息',
      key: 'variants',
      render: (_, record) => {
        if (!record.variants) {
          return <Text type="secondary">-</Text>;
        }
        
        // 判断是否为单品
        const isSimple = !record.product?.variant_types || record.product.variant_types.length === 0;
        
        if (isSimple) {
          // 单品
          if (Array.isArray(record.variants) && record.variants.length > 0) {
            const variant = record.variants[0];
            return (
              <div>
                <div>特殊价格: {variant.currency} {variant.price}</div>
                <div>库存: {variant.stock === -1 ? '无限' : variant.stock}</div>
                <div>SKU: {variant.SKU}</div>
              </div>
            );
          } else {
            return <Text type="secondary">无规格信息</Text>;
          }
        } else {
          // 多规格
          const variantCount = countVariants(record.variants);
          return (
            <div>
              <Tag color="blue">{variantCount} 个规格</Tag>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {(record.product?.variant_types || []).join(', ')}
              </div>
            </div>
          );
        }
      },
    },
    {
      title: '状态',
      dataIndex: 'enable',
      key: 'enable',
      render: (enable, record) => (
        <Switch 
          checked={enable} 
          onChange={() => handleToggleEnable(record.id)}
        />
      ),
      width: '8%',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleString('zh-CN'),
      width: '15%',
    },
    {
      title: '操作',
      key: 'action',
      width: '15%',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个特殊价格记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 'bold' }}>按用户报价</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          创建单用户报价
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={specialPrices}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        size="middle"
      />

      {/* 创建/编辑对话框 */}
      <Modal
        title={isEditMode ? '编辑特殊价格' : '创建单用户报价'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={900}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <Form.Item
                label="选择用户"
                required
                style={{ marginBottom: 0 }}
              >
                <AutoComplete
                  value={userSearchInput}
                  options={userSelectOptions}
                  onSelect={(value, option) => {
                    handleUserSelect(value, option);
                    setUserSearchInput(option?.label ?? '');
                  }}
                  onChange={(value, option) => {
                    setUserSearchInput(value);
                    if (!value) {
                      setUserOptions([]);
                      handleUserSelect('', null);
                    } else if (!option) {
                      setSelectedUser(null);
                      form.setFieldsValue({ dropshipper_id: undefined });
                    }
                  }}
                  notFoundContent={userSearchLoading ? <Spin size="small" /> : null}
                  style={{ width: '100%' }}
                  filterOption={false}
                  disabled={isEditMode}
                >
                  <Search
                    placeholder="输入用户名搜索用户"
                    enterButton={<SearchOutlined />}
                    allowClear
                    disabled={isEditMode}
                    loading={userSearchLoading}
                    onSearch={handleUserSearch}
                  />
                </AutoComplete>
              </Form.Item>
              <Form.Item
                name="dropshipper_id"
                rules={[{ required: true, message: '请选择用户' }]}
                style={{ marginBottom: 0, height: 0, overflow: 'hidden' }}
              >
                <Input type="hidden" />
              </Form.Item>
            </div>

            <div style={{ flex: 1 }}>
              <Form.Item
                label="选择商品"
                required
              >
                <Button 
                  type="dashed" 
                  onClick={openProductSelector}
                  disabled={isEditMode}
                  style={{ width: '100%' }}
                >
                  {productDetails ? '重新选择商品' : '选择商品'}
                </Button>
              </Form.Item>
              <Form.Item
                name="product_id"
                rules={[{ required: true, message: '请选择商品' }]}
                style={{ marginBottom: 0, height: 0, overflow: 'hidden' }}
              >
                <Input type="hidden" />
              </Form.Item>
            </div>
          </div>

          {/* 显示已选择的商品信息 */}
          {productDetails && (
            <div style={{ 
              marginBottom: 16, 
              padding: '12px', 
              backgroundColor: '#f6ffed', 
              borderRadius: '6px',
              border: '1px solid #b7eb8f'
            }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                {/* 商品图片 */}
                <div style={{ width: '80px', flexShrink: 0 }}>
                  {productDetails.images && productDetails.images.length > 0 ? (
                    <Image
                      src={`${API_BASE_URL}/products/${productDetails.id}/images/${
                        productDetails.images.find(img => img.id === productDetails.cover_picture)?.id || 
                        productDetails.images[0]?.id
                      }`}
                      width={80}
                      height={80}
                      style={{ objectFit: 'cover', borderRadius: '6px' }}
                      preview={false}
                    />
                  ) : (
                    <div style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#999',
                      fontSize: '12px'
                    }}>
                      无图片
                    </div>
                  )}
                </div>
                
                {/* 商品信息 */}
                <div style={{ flex: 1 }}>
                  <div style={{ marginBottom: '8px' }}>
                    <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                      {productDetails.name}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div>
                      <Text strong>商品ID: </Text>
                      <Text type="secondary">{productDetails.id}</Text>
                    </div>
                    {productDetails.category?.name && (
                      <div>
                        <Text strong>分类: </Text>
                        <Tag color="blue">{productDetails.category.name}</Tag>
                      </div>
                    )}
                    <div>
                      <Text strong>SKU数量: </Text>
                      <Text type="secondary">{variants.length}</Text>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {productDetails && variants.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ margin: 0, color: '#1890ff' }}>SKU价格设置</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <InputNumber
                    value={bulkPricePercentage}
                    onChange={setBulkPricePercentage}
                    min={0}
                    max={100}
                    precision={0}
                    style={{ width: '80px' }}
                    addonAfter="%"
                    placeholder="20"
                  />
                  <Button 
                    type="primary" 
                    size="small"
                    style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
                    onClick={() => handleBulkPriceAdjustment(true)}
                    disabled={variants.length === 0}
                  >
                    加价
                  </Button>
                  <Button 
                    type="primary" 
                    size="small"
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    onClick={() => handleBulkPriceAdjustment(false)}
                    disabled={variants.length === 0}
                  >
                    减价
                  </Button>
                </div>
              </div>
              <div style={{ 
                maxHeight: '500px', 
                overflowY: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: '6px',
                backgroundColor: '#fafafa'
              }}>
                {variants.map((variant, index) => (
                  <div 
                    key={index}
                    style={{ 
                      padding: '16px', 
                      marginBottom: index === variants.length - 1 ? 0 : '1px',
                      backgroundColor: 'white',
                      display: 'grid',
                      gridTemplateColumns: 'auto 1fr auto',
                      gap: '16px',
                      alignItems: 'center'
                    }}
                  >
                    {/* SKU图片 */}
                    <div style={{ width: '80px', textAlign: 'center' }}>
                      {variant.image ? (
                        <Image
                          src={`${API_BASE_URL}/products/${productDetails.id}/images/${variant.image}`}
                          width={70}
                          height={70}
                          style={{ objectFit: 'cover', borderRadius: '6px' }}
                          preview={false}
                        />
                      ) : (
                        <div style={{
                          width: '70px',
                          height: '70px',
                          backgroundColor: '#f0f0f0',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#999',
                          fontSize: '12px'
                        }}>
                          无图片
                        </div>
                      )}
                    </div>
                    
                    {/* SKU信息（只读） */}
                    <div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong style={{ color: '#1890ff' }}>SKU: </Text>
                        <Text code>{variant.SKU || '-'}</Text>
                      </div>
                      {variant.variant_path && variant.variant_path.length > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          <Text strong>规格: </Text>
                          <Tag color="blue">{variant.variant_path.join(' - ')}</Tag>
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                        <div>
                          <Text strong>原价: </Text>
                          <Text type="secondary">{variant.currency} {variant.price}</Text>
                        </div>
                        <div>
                          <Text strong>库存: </Text>
                          <Text type="secondary">{variant.stock === -1 ? '无限' : variant.stock}</Text>
                        </div>
                        <div>
                          <Text strong>重量: </Text>
                          <Text type="secondary">{variant.weight || 100}g</Text>
                        </div>
                      </div>
                    </div>
                    
                    {/* 价格编辑区域 */}
                    <div style={{ 
                      textAlign: 'center',
                      padding: '12px',
                      backgroundColor: '#f6ffed',
                      borderRadius: '6px',
                      border: '1px solid #b7eb8f',
                      minWidth: '160px'
                    }}>
                      <div style={{ marginBottom: '8px' }}>
                        <Text strong style={{ color: '#52c41a' }}>该用户价格</Text>
                      </div>
                      <InputNumber
                        value={variant.price}
                        onChange={(value) => handleVariantPriceChange(index, value)}
                        min={0}
                        precision={2}
                        style={{ width: '130px' }}
                        addonBefore={<span style={{ color: '#666' }}>{variant.currency}</span>}
                        placeholder="设置价格"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Form.Item
            name="enable"
            label="是否启用"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                disabled={!productDetails || variants.length === 0}
              >
                {isEditMode ? '更新' : '创建'}
              </Button>
            </Space>
            {(!productDetails || variants.length === 0) && (
              <div style={{ marginTop: 8, color: '#999', fontSize: '12px' }}>
                请先选择商品并加载SKU信息
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      {/* 商品选择器模态框 */}
      <Modal
        title="选择商品"
        open={productSelectorVisible}
        onCancel={closeProductSelector}
        footer={null}
        width={780}
        destroyOnClose
      >
        <ProductSearchSelector
          multiple={false}
          onSelect={handleProductSelected}
        />
      </Modal>
    </div>
  );
};

export default SpecialPrice;
